import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { PopoverClose } from "@radix-ui/react-popover";
import {
  MoreHorizontal,
  Trash2,
  Eye,
  CircleOff,
  Circle,
  Power,
  PowerOff,
} from "lucide-react";

export const createColumns = (
  onDeleteClick,
  onEditClick = null,
  onToggleBlock = null,
  onToggleActive = null
) => [
  {
    accessorKey: "name",
    header: "Name",
    cell: ({ row }) => (
      <div className="max-w-xs truncate" title={row.name || "No name"}>
        {row.name || <span className="text-muted-foreground">No name</span>}
      </div>
    ),
  },
  {
    accessorKey: "size",
    header: "Size",
    cell: ({ row }) => (
      <span className="font-mono text-xs">
        {row.size || <span className="text-muted-foreground">-</span>}
      </span>
    ),
  },
  {
    accessorKey: "wilaya",
    header: "Wilaya",
    cell: ({ row }) => (
      <span className="text-xs">
        {row.wilaya || <span className="text-muted-foreground">-</span>}
      </span>
    ),
  },
  {
    accessorKey: "code",
    header: "Code",
    cell: ({ row }) => {
      const code = row.code || {};

      return (
        <span className="font-mono text-xs" title={code.fullCode || ""}>
          {code.fullCode || "-"}
          {code.status === "active" ? (
            <Badge variant="default" className="ml-2 text-xs">
              Active
            </Badge>
          ) : (
            <Badge variant="destructive" className="ml-2 text-xs">
              Blocked
            </Badge>
          )}
        </span>
      );
    },
  },
  {
    accessorKey: "hardwareId",
    header: "Hardware Id",
    cell: ({ row }) => {
      const hardwareId = row?.hardwareId || "NOT SET";

      return (
        <span className="font-mono text-xs" title={hardwareId || ""}>
          {hardwareId || "-"}
        </span>
      );
    },
  },
  {
    accessorKey: "ip_whitelist",
    header: "IP Whitelist",
    hidden: true,
    cell: ({ row }) => {
      const ips = row.ip_whitelist;
      if (!ips || ips.length === 0) {
        return <span className="text-muted-foreground">No IPs</span>;
      }
      if (ips.length === 1) {
        return <span className="font-mono text-xs">{ips[0]}</span>;
      }
      return (
        <div className="flex items-center gap-1">
          <span className="font-mono text-xs">{ips[0]}</span>
          <Badge variant="secondary" className="text-xs">
            +{ips.length - 1}
          </Badge>
        </div>
      );
    },
  },
  {
    accessorKey: "isActive",
    header: "Status",
    cell: ({ row }) => (
      <>
        {row.isActive ? (
          <Badge variant="default" className="text-xs">
            Active
          </Badge>
        ) : (
          <Badge variant="destructive" className="text-xs">
            Inactive
          </Badge>
        )}
      </>
    ),
  },
  {
    accessorKey: "last_seen",
    header: "Last Seen",
    cell: ({ row }) => (
      <div className="text-sm text-muted-foreground">
        {row.last_seen ? new Date(row.last_seen).toLocaleDateString() : "Never"}
      </div>
    ),
  },
  {
    accessorKey: "created_at",
    header: "Created",
    cell: ({ row }) => (
      <div className="text-sm text-muted-foreground">
        {row.created_at ? new Date(row.created_at).toLocaleDateString() : ""}
      </div>
    ),
  },
  {
    accessorKey: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0" size="sm">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-40">
          <div className="flex flex-col space-y-1">
            {onEditClick && (
              <Button
                variant="ghost"
                size="sm"
                className="justify-start"
                onClick={() => onEditClick(row)}
              >
                <Eye className="mr-2 h-4 w-4" />
                View
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "justify-start flex items-center",
                row.code?.status === "active"
                  ? "text-orange-600 hover:text-orange-700"
                  : "text-green-600 hover:text-green-700"
              )}
              onClick={() => {
                onToggleBlock(row);
              }}
            >
              {row.code?.status === "active" ? (
                <CircleOff className="mr-2 h-4 w-4" />
              ) : (
                <Circle className="mr-2 h-4 w-4" />
              )}
              {row.code?.status === "active" ? "Block Code" : "Activate Code"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "justify-start flex items-center",
                row.isActive
                  ? "text-orange-600 hover:text-orange-700"
                  : "text-green-600 hover:text-green-700"
              )}
              onClick={() => {
                onToggleActive(row);
              }}
            >
              {row.isActive ? (
                <PowerOff className="mr-2 h-4 w-4" />
              ) : (
                <Power className="mr-2 h-4 w-4" />
              )}
              {row.isActive ? "Mark Inactive" : "Mark Active"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="justify-start text-red-600 hover:text-red-700"
              onClick={() => onDeleteClick(row)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    ),
    enableSorting: false,
  },
];
